/**
 * QACheckBadge — Color-coded status badge for pass/warn/fail.
 *
 * Uses project theme tokens:
 *   Pass: teal tokens
 *   Warn: amber/yellow tones
 *   Fail: destructive tokens
 */

import { cn } from '@/lib/utils';
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import type { QACheckStatus, QAOverallResult, QARunStatus } from '@/types/qaContentChecks';

interface QACheckBadgeProps {
  status: QACheckStatus | QAOverallResult | QARunStatus | null;
  size?: 'sm' | 'md';
  showIcon?: boolean;
  showLabel?: boolean;
}

const STATUS_CONFIG = {
  pass: {
    label: 'Pass',
    icon: CheckCircle2,
    badgeClass: 'bg-teal-light text-teal-dark border-teal/20',
    iconClass: 'text-teal',
  },
  warn: {
    label: 'Warn',
    icon: AlertTriangle,
    badgeClass: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    iconClass: 'text-amber-500 dark:text-amber-400',
  },
  fail: {
    label: 'Fail',
    icon: XCircle,
    badgeClass: 'bg-destructive/5 text-destructive border-destructive/20',
    iconClass: 'text-destructive',
  },
  error: {
    label: 'Error',
    icon: XCircle,
    badgeClass: 'bg-destructive/5 text-destructive border-destructive/20',
    iconClass: 'text-destructive',
  },
  pending: {
    label: 'Pending',
    icon: Loader2,
    badgeClass: 'bg-muted text-muted-foreground border-border',
    iconClass: 'text-muted-foreground animate-spin',
  },
  running: {
    label: 'Running',
    icon: Loader2,
    badgeClass: 'bg-teal-light text-teal-dark border-teal/20',
    iconClass: 'text-teal animate-spin',
  },
  completed: {
    label: 'Done',
    icon: CheckCircle2,
    badgeClass: 'bg-muted text-muted-foreground border-border',
    iconClass: 'text-muted-foreground',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    badgeClass: 'bg-destructive/5 text-destructive border-destructive/20',
    iconClass: 'text-destructive',
  },
} as const;

export function QACheckBadge({
  status,
  size = 'sm',
  showIcon = true,
  showLabel = true,
}: QACheckBadgeProps) {
  const config = status ? STATUS_CONFIG[status] : STATUS_CONFIG.pending;
  const Icon = config.icon;
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        config.badgeClass,
      )}
    >
      {showIcon && <Icon className={cn(iconSize, config.iconClass)} />}
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}
